import { Injectable } from '@angular/core';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';

@Injectable({
  providedIn: 'root',
})
export class CustomGlobalFilterService extends GlobalFilterService {
  private readonly traceTag = '[REP-MAP-DBG]';

  private normalizeNestedFilterFields(container: any): void {
    if (!container) return;

    Object.keys(container).forEach((key) => {
      const fieldValue = container[key];
      if (fieldValue === null || fieldValue === undefined || fieldValue === '') return;

      if (Array.isArray(fieldValue)) {
        if (fieldValue.length === 0) return;
        if (Array.isArray(fieldValue[0])) return;
        container[key] = [fieldValue];
        return;
      }

      container[key] = [[fieldValue]];
    });
  }

  private formatFilterPayloadValue(key: string, value: any): any[] {
    const asArray = Array.isArray(value) ? value : [value];

    const isAlreadyNestedArray =
      asArray.length > 0 && Array.isArray(asArray[0]);

    return isAlreadyNestedArray ? asArray : [asArray];
  }

  /**
   * Overrides updateFilterSelection to handle 'values' being a string instead of an array.
   * All other methods are inherited from GlobalFilterService.
   */
  override updateFilterSelection(
    keyOrKeys: string | string[],
    values: any[] | any, // Allow non-array inputs
    isQueryParam = false,
    widgetId?: string,
    emit: boolean = true,
    filterListener?: string,
  ): void {
    const traceId = `SEL-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const keys = Array.isArray(keyOrKeys)
      ? keyOrKeys
      : (keyOrKeys || '')
          .split(',')
          .map((k) => k.trim())
          .filter((k) => k);

    keys.forEach((key) => {
      // CUSTOM CHANGE: Check if values is array. If not, use it directly.
      const valueToStore = Array.isArray(values) ? values?.[0] || null : values;

      this.selectedFilters[key] = valueToStore;

      if (isQueryParam) {
        this.filters.queryParams[key] = values;
      } else {
        this.filters.requestParams[key] = values;
      }
    });

    this.widgetId = widgetId || null;
    try {
      console.log(`${this.traceTag} ${traceId} updateFilterSelection`, {
        keys,
        values,
        isQueryParam,
        widgetId,
        filterListener,
        requestParams: this.filters.requestParams,
        queryParams: this.filters.queryParams,
      });
    } catch (_e) {
      // ignore
    }
    if (emit) {
      const filterData = {
        ...this.filters,
        widgetId: this.widgetId,
        changedFilterKey: keys.length === 1 ? keys[0] : keys,
        filterListener,
      };

      this.filterSubject.next(filterData);
      this.currentSelectedFilter.next({ key: keys });
    }
  }

  /**
   * Overrides buildPayload to merge active filters into requestData
   * Supports both nested filter object format and flat requestData format
   */
  override buildPayload(config: any, filters: any[]): any {
    const traceId = `PAY-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    if (config?.skipDefaultPayload && config?.customPayload) {
      // Deep clone to avoid mutating the original config
      const payload = JSON.parse(JSON.stringify(config.customPayload));
      // Never send seeded JSON response payload as request input.
      if ('responseList' in payload) {
        delete payload.responseList;
      }
      // Backend contract expects responseList field to exist as an empty array.
      // Keep it empty to avoid using seeded JSON options while preserving request shape.
      payload.responseList = [];
      
      if (payload.requestData) {
        const requestParams = this.filters.requestParams || {};
        
        // Determine if we should use nested filter format or flat format
        const hasNestedFilter = payload.requestData.filter !== undefined;
        
        if (hasNestedFilter) {
          // Format 1: Nested filter object { "requestData": { "filter": { "region": [...] } } }
          if (!payload.requestData.filter) {
            payload.requestData.filter = {};
          }
          this.normalizeNestedFilterFields(payload.requestData.filter);
          
          // Merge selected filters into requestData.filter
          Object.keys(requestParams).forEach((key) => {
            const value = requestParams[key];
            
            // Skip empty/null values
            if (value === null || value === undefined || value === '') return;
            if (Array.isArray(value) && value.length === 0) return;
            
            // Normalize values to primitives if options are objects
            let normalizedValue = value;
            if (Array.isArray(value)) {
              normalizedValue = value.map((v) => {
                if (typeof v !== 'object') return v;
                if ('value' in v) return v.value;
                if ('key' in v) return v.key;
                if ('id' in v) return v.id;
                if ('label' in v) return v.label;
                return JSON.stringify(v);
              });
            } else if (typeof value === 'object') {
              if ('value' in value) normalizedValue = value.value;
              else if ('key' in value) normalizedValue = value.key;
              else if ('id' in value) normalizedValue = value.id;
              else if ('label' in value) normalizedValue = value.label;
            }
            
            // Add to filter object (as array for consistency)
            payload.requestData.filter[key] = this.formatFilterPayloadValue(key, normalizedValue);
          });
        } else {
          // Format 2: Flat requestData { "requestData": { "region": "GLOBAL", "mine_outlook_score": [...] } }
          this.normalizeNestedFilterFields(payload.requestData);
          Object.keys(requestParams).forEach((key) => {
            const value = requestParams[key];
            
            // Skip empty/null values
            if (value === null || value === undefined || value === '') return;
            if (Array.isArray(value) && value.length === 0) return;
            
            // Normalize values to primitives if options are objects
            let normalizedValue = value;
            if (Array.isArray(value)) {
              normalizedValue = value.map((v) => {
                if (typeof v !== 'object') return v;
                if ('value' in v) return v.value;
                if ('key' in v) return v.key;
                if ('id' in v) return v.id;
                if ('label' in v) return v.label;
                return JSON.stringify(v);
              });
            } else if (typeof value === 'object') {
              if ('value' in value) normalizedValue = value.value;
              else if ('key' in value) normalizedValue = value.key;
              else if ('id' in value) normalizedValue = value.id;
              else if ('label' in value) normalizedValue = value.label;
            }
            
            // Add directly to requestData
            payload.requestData[key] = this.formatFilterPayloadValue(key, normalizedValue);
          });
        }
      }
      try {
        console.log(`${this.traceTag} ${traceId} buildPayload(skipDefaultPayload=true)`, {
          configWidgetId: config?.widgetId,
          requestParams: this.filters.requestParams,
          finalRequestData: payload.requestData,
          hasResponseList: !!payload.responseList,
        });
      } catch (_e) {
        // ignore
      }
      return payload;
    }

    // Fallback to default behavior
    try {
      console.log(`${this.traceTag} ${traceId} buildPayload(fallback->super)`, {
        configWidgetId: config?.widgetId,
        requestParams: this.filters.requestParams,
      });
    } catch (_e) {
      // ignore
    }
    return super.buildPayload(config, filters);
  }
}
